import React, {useContext} from 'react'
import {useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {HotelCardContext} from 'components/HotelCardContext'
import {PriceWatchCTAWrapper} from 'components/PriceWatchCTAWrapper/PriceWatchCTAWrapper'
import {useOtaMode} from 'hooks/useOtaMode'
import {getAnchorComplete, getHotel} from 'modules/sapiSearch/selectors'

import {Box} from '@vio/atlas/helpers/Box'
import {cssTheme} from '@vio/atlas/themes'
import {PriceWatchBell} from '@vio/shared/src/priceWatch/components/PriceWatchBell'
import {PriceWatchToggle} from '@vio/shared/src/priceWatch/components/PriceWatchToggle/PriceWatchToggle'

const GALLERY_WIDTH_DESKTOP = 278
const TOP_OFFER_WIDTH_DESKTOP = 232
const MIN_CARD_INNER_HEIGHT = 237

interface Props {
  /** If the hotel is the anchor hotel */
  isAnchor?: boolean

  /** The component to render for the gallery */
  galleryComponent: React.ReactNode

  /** The component to render for the property summary */
  propertySummaryComponent: React.ReactNode

  /** The component to render for the offer list */
  offerListComponent: React.ReactNode

  /** The component to render for the top offer */
  topOfferComponent: React.ReactNode

  /** The component to render for the availability pane */
  availabilityPaneComponent: React.ReactNode

  /** The component to render for anchor hotel*/
  anchorLabelComponent?: React.ReactNode
}

const TopOfferWrapper = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  grid-column: 3 / 4;
  grid-row: 1 / 2;
  &.--left-border {
    border-left: 1px solid ${cssTheme.colors.border.neutral.secondary};
    padding-left: ${cssTheme.layout.spacing.s500};
    padding-bottom: ${cssTheme.layout.spacing.s300};
  }
  /* solution for OfferLinkWrapper that adds a <span> and an <a> around the offer */
  > * {
    width: 100% !important;
  }
`

const PriceWatchWrapper = styled.div`
  margin: ${cssTheme.layout.spacing.s400} 0 ${cssTheme.layout.spacing.s100};

  &:empty {
    margin: 0;
  }
`

/**
 * The base skeleton of the hotel card
 */
export const BaseHotelCard: React.FC<Props> = ({
  isAnchor = false,
  galleryComponent,
  propertySummaryComponent,
  offerListComponent,
  topOfferComponent,
  availabilityPaneComponent,
  anchorLabelComponent
}) => {
  const {hotelId} = useContext(HotelCardContext)
  const hotel = useSelector(state => getHotel(state, hotelId))
  const anchorComplete = useSelector(getAnchorComplete)
  const shouldShowPriceWatchToggle = isAnchor && anchorComplete
  const {isOTAMode: shouldHideSecondaryOffers} = useOtaMode()

  const withSelectedLabel = isAnchor && !!anchorLabelComponent
  const anchorBorderColor = withSelectedLabel
    ? 'border.interactive.primary'
    : 'hotelCard.special.border'

  return (
    <Box
      borderRadius="xxl"
      marginTop={isAnchor ? 's200' : 's000'}
      borderStyle="solid"
      borderWidth="bw200"
      borderColor={isAnchor ? anchorBorderColor : 'transparent'}
      overflow="hidden"
    >
      {withSelectedLabel && anchorLabelComponent}
      <Box padding="s400">
        <Box
          minHeight={`${MIN_CARD_INNER_HEIGHT}px`}
          display="grid"
          gridAutoFlow="column"
          gridAutoRows="1fr auto"
          gridTemplateColumns={`${GALLERY_WIDTH_DESKTOP}px 1fr ${TOP_OFFER_WIDTH_DESKTOP}px`}
          position="relative"
        >
          <Box height="100%" gridColumn="1 / 2" gridRow="1 / 3">
            {galleryComponent}
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            height="100%"
            gridColumn="2 / 3"
            gridRow="1 / 2"
            overflow="hidden"
          >
            <Box
              height="100%"
              paddingLeft="s500"
              paddingRight="s500"
              marginBottom="s400"
            >
              {propertySummaryComponent}
            </Box>
            {!shouldHideSecondaryOffers && <div>{offerListComponent}</div>}
          </Box>
          <TopOfferWrapper
            className={cx(!availabilityPaneComponent && '--left-border')}
          >
            {topOfferComponent}
          </TopOfferWrapper>
          {availabilityPaneComponent}
          <Box position="absolute" left="12px" top="12px" zIndex="1">
            <PriceWatchCTAWrapper
              hotel={hotel}
              ctaComponent={PriceWatchBell}
              componentName="PriceWatchBell"
              searchIsCompleted
            />
          </Box>
        </Box>

        {shouldShowPriceWatchToggle && (
          <PriceWatchWrapper>
            <PriceWatchCTAWrapper
              hotel={hotel}
              shouldPulse={false}
              ctaComponent={PriceWatchToggle}
              componentName="PriceWatchToggle"
              searchIsCompleted
            />
          </PriceWatchWrapper>
        )}
      </Box>
    </Box>
  )
}
